import { getAuth } from 'firebase/auth';
import { cloudRunURLs, environmentType, deploymentEnvironment, emulatorPorts } from '../../config';
import { AiUsageLimitExceededError, isAiUsageLimitExceededPayload } from '#types';
import type { CallRunLanguageModelCallbacks, RequestUnion } from './types';
import { cloudRunHandlers } from './handlers';

const CLOUD_RUN_READY_ATTEMPTS = 30;
const CLOUD_RUN_READY_BACKOFF_MS = 1000;

/**
 * Waits for the local Cloud Run emulator to answer `/healthz`. The container
 * (`cloud_run/runLanguageModel/emulator.sh`) can still be booting when an AI op
 * fires; without this the call would race the cold start and fail.
 */
const waitForLocalCloudRun = async (baseUrl: string): Promise<void> => {
    const attempt = async (n: number): Promise<void> => {
        const response = await fetch(`${baseUrl}/healthz`).catch(() => null);
        if (response?.ok) return;
        if (n >= CLOUD_RUN_READY_ATTEMPTS) {
            throw new Error(`Local Cloud Run emulator not reachable at ${baseUrl}. Start it with cloud_run/runLanguageModel/emulator.sh`);
        }
        await new Promise((resolve) => setTimeout(resolve, CLOUD_RUN_READY_BACKOFF_MS));
        return attempt(n + 1);
    };
    return attempt(1);
};

/** Returns the Cloud Run URL, waiting for the local emulator when running locally */
const getCloudRunURL = async (): Promise<string> => {
    if (environmentType === 'development' && deploymentEnvironment === 'local') {
        const cloudRunBaseUrl = `http://localhost:${emulatorPorts.cloudRun}`;
        await waitForLocalCloudRun(cloudRunBaseUrl);
        return `${cloudRunBaseUrl}/runlanguagemodel`;
    }
    return cloudRunURLs.runLanguageModel as string;
};

const fetchLanguageModel = async (requestData: RequestUnion, signal?: AbortSignal): Promise<Response> => {
    const url = await getCloudRunURL();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error('User not logged in');
    }

    const send = async (forceRefresh: boolean): Promise<Response> => {
        const idToken = await user.getIdToken(forceRefresh);
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: requestData, idToken }),
            signal,
        });
    };

    const firstResponse = await send(false);
    const response = firstResponse.status === 401 ? await send(true) : firstResponse;

    if (response.status === 429) {
        const body = await response.clone().json().catch(() => null);
        if (isAiUsageLimitExceededPayload(body)) {
            throw new AiUsageLimitExceededError({ window: body.window, scope: body.scope });
        }
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw Object.assign(new Error(`HTTP error! status: ${response.status}${detail ? ` — ${detail}` : ''}`), { status: response.status });
    }

    return response;
};

export type { CallRunLanguageModelCallbacks } from './types';

const linkSignal = (controller: AbortController, external?: AbortSignal): void => {
    if (!external) return;
    if (external.aborted) {
        controller.abort();
        return;
    }
    external.addEventListener('abort', () => controller.abort(), { once: true });
};

export const callRunLanguageModel = async (
    requestData: RequestUnion,
    callbacks?: CallRunLanguageModelCallbacks
) => {
    const cb = callbacks ?? {};
    const controller = new AbortController();
    linkSignal(controller, cb.signal);

    try {
        const response = await fetchLanguageModel(requestData, controller.signal);
        for (const handler of cloudRunHandlers) {
            if (handler.isApplicable(requestData)) {
                return await handler.handle(response, requestData, { ...cb, abort: () => controller.abort() });
            }
        }
        throw new Error('No handler for request');
    } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
            cb.onComplete?.();
            return;
        }
        console.error('Error calling the language model service:', error);
        if (cb.onError) {
            cb.onError(error as Error);
            return;
        }
        throw error;
    }
};
